import { useState, useEffect } from "react";
import { Panel, Label } from "@/components/SplitLayout";
import CopyButton from "@/components/CopyButton";

const ALGOS = ["SHA-1", "SHA-256", "SHA-384", "SHA-512"];

const hashText = async (algo, text) => {
  const data = new TextEncoder().encode(text);
  const buf = await crypto.subtle.digest(algo, data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

export default function HashGenerator() {
  const [text, setText] = useState("");
  const [results, setResults] = useState({});

  useEffect(() => {
    let cancelled = false;
    if (!text) {
      setResults({});
      return;
    }
    Promise.all(ALGOS.map((a) => hashText(a, text).then((h) => [a, h]))).then((pairs) => {
      if (!cancelled) setResults(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [text]);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="space-y-2">
        <Label>Text to hash</Label>
        <textarea
          data-testid="hash-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type or paste text — hashes update live…"
          className="h-[360px] w-full resize-none border border-border bg-card p-5 text-sm outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <div className="lg:sticky lg:top-24 lg:self-start space-y-4">
        {ALGOS.map((algo) => (
          <Panel
            key={algo}
            title={algo}
            testId={`hash-panel-${algo.toLowerCase()}`}
            action={results[algo] ? <CopyButton text={results[algo]} /> : null}
          >
            <pre
              data-testid={`hash-${algo.toLowerCase()}`}
              className="overflow-auto whitespace-pre-wrap break-all font-mono text-xs leading-relaxed"
            >
              {results[algo] || <span className="text-muted-foreground">—</span>}
            </pre>
          </Panel>
        ))}
      </div>
    </div>
  );
}
